import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

type SearchParams = {
  searchBy: string;
  searchValue: string;
  filter?: string | string[];
  page: number;
  recordsPerPage: number;
};

type SetSearchParams<T extends SearchParams> = (nextValues: Partial<T>) => void;

const defaultSearchValues = {
  searchBy: '',
  searchValue: '',
  page: 1,
  recordsPerPage: 5,
} as const satisfies SearchParams;

const convertToUrlFormat = ({
  searchBy,
  searchValue,
  filter,
  page,
  recordsPerPage,
}: SearchParams): Record<keyof SearchParams, string> =>
  Object.assign(
    {
      searchBy,
      searchValue,
      page: String(page),
      recordsPerPage: String(recordsPerPage),
    },
    typeof filter !== 'undefined'
      ? { filter: Array.isArray(filter) ? filter.join(',') : filter }
      : ({} as { filter: never }),
  );

const parseFromUrlFormat = ({
  page,
  recordsPerPage,
  searchBy,
  searchValue,
  filter,
}: Record<string, string>): Partial<SearchParams> =>
  Object.assign(
    { searchBy, searchValue },
    page ? { page: parseInt(page) } : {},
    recordsPerPage ? { recordsPerPage: parseInt(recordsPerPage) } : {},
    filter
      ? {
          filter: filter.includes(',') ? filter.split(',') : filter,
        }
      : {},
  );

function useSearchParamsInUrl<Params extends SearchParams = SearchParams>(
  initialValues: Partial<Params> | false = {},
): [Params, SetSearchParams<Params>] {
  const initialState = Object.assign({}, defaultSearchValues, initialValues === false ? {} : initialValues);

  const [rawSearchParams, setSearchParams] = useSearchParams(convertToUrlFormat(initialState));

  const parsedSearchParams = parseFromUrlFormat(Object.fromEntries(rawSearchParams.entries())) as Params;
  const searchParams = Object.assign({}, defaultSearchValues, parsedSearchParams);

  const setNextValues = (nextValues: Partial<Params>) =>
    setSearchParams((params) => {
      Object.entries(nextValues).forEach(([key, value]) => {
        typeof value !== 'undefined' && params.set(key, value.toString());
      });
      return params;
    });

  useEffect(() => {
    if (initialValues === false) return;

    setNextValues(parsedSearchParams);
  }, []);

  return [searchParams, setNextValues];
}

export { useSearchParamsInUrl };
